import {
  CategoryScale,
  Chart as ChartJS,
  type ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import dayjs from "dayjs";
import { useMemo } from "react";
import { Line } from "react-chartjs-2";

import { formatMoneyAmount } from "@krea/lender-features/utils/formatMoneyAmount";

import type { IAccountingDataResponse } from "@/features/lender-applications/types/lender-application";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const options = (currency: string | null): ChartOptions<"line"> => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "top",
      align: "start",
      labels: {
        boxHeight: 0,
        boxWidth: 17,
        font: {
          size: 12,
        },
        color: "#000",
        padding: 15,
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: true,
        // drawBorder: false,
        drawTicks: false,
        color: "#F0F0F0",
      },
      ticks: {
        padding: 10,
      },
    },
    y: {
      grid: {
        display: true,
        // drawBorder: false,
        drawTicks: false,
        color: "#F0F0F0",
      },
      beginAtZero: true,

      ticks: {
        callback: (value) =>
          formatMoneyAmount(Number(value), {
            digits: 1,
            currency: currency ?? "",
          }),
        padding: 10,
      },
    },
  },
});

interface IAccountingDataChartProps {
  profitLossEntries: IAccountingDataResponse["profitLossEntries"];
  currency: string | null;
}

export const AccountingDataChart = ({
  profitLossEntries,
  currency,
}: IAccountingDataChartProps) => {
  const [labels, revenues, ebitda, currentProfit] = useMemo(
    () =>
      profitLossEntries.reduceRight<
        [number[], (number | null)[], (number | null)[], (number | null)[]]
      >(
        ([years, revenuesValues, ebitdaValues, currentProfitValues], pl) => {
          const year = dayjs(pl.financialYearEnd).year();
          years.push(year);
          revenuesValues.push(pl.revenue);
          ebitdaValues.push(pl.ebitda);
          currentProfitValues.push(pl.currentProfit);

          return [years, revenuesValues, ebitdaValues, currentProfitValues];
        },
        [[], [], [], []],
      ),
    [profitLossEntries],
  );

  const data = {
    labels,
    datasets: [
      {
        label: "Revenue",
        data: revenues,
        borderColor: "#1F78B4",
        backgroundColor: "#1F78B4",
      },
      {
        label: "EBITDA",
        data: ebitda,
        borderColor: "#FF8B00",
        backgroundColor: "#FF8B00",
      },
      {
        label: "Profit before Tax",
        data: currentProfit,
        borderColor: "#802392",
        backgroundColor: "#802392",
      },
    ],
  };

  return (
    <div style={{ height: 250 }}>
      <Line options={options(currency)} data={data} />
    </div>
  );
};
